/**
 * Texts of the sign-in screen and of the player's account (port addition: the original signed in through VK). As
 * everywhere in the game, the Russian text is the source and the translation key (src/game/runtime/i18n.ts): the
 * game's text fields translate it on assignment, and every string has a catalogue entry in uk / en
 * (tools/i18n/extra.json). The messages of the credential problems use the stable `details.reason` values of the
 * protocol (src/shared/auth-rules.ts), so the instant client-side check and the server's answer read the same.
 */
import type { CredentialProblemReason } from '../../shared/auth-rules';
import type { EmailProblem } from '../../shared/email-rules';

// ---------------------------------------------------------------- the three views of the sign-in screen
export const MSG_AUTH_WELCOME = 'Добро пожаловать!';
export const MSG_AUTH_WELCOME_TEXT = 'Войдите в свой аккаунт или создайте новый. Прогресс хранится на сервере, играть можно с любого устройства.';
/** The welcome text with the backend inside the page (?backend=offline / memory): accounts live in this browser. */
export const MSG_AUTH_WELCOME_TEXT_LOCAL = 'Войдите в свой аккаунт или создайте новый. Прогресс хранится в этом браузере.';
export const MSG_AUTH_SIGN_IN = 'Войти';
export const MSG_AUTH_SIGN_UP = 'Регистрация';
export const MSG_AUTH_SIGN_IN_TITLE = 'Вход в игру';
export const MSG_AUTH_SIGN_UP_TITLE = 'Новый аккаунт';
export const MSG_AUTH_CREATE = 'Создать аккаунт';
export const MSG_AUTH_NICKNAME = 'Никнейм';
export const MSG_AUTH_PASSWORD = 'Пароль';
export const MSG_AUTH_SHOW_PASSWORD = 'показать';
export const MSG_AUTH_HIDE_PASSWORD = 'скрыть';
export const MSG_AUTH_BACK = 'назад';
export const MSG_AUTH_TO_SIGN_UP = 'Нет аккаунта? Зарегистрируйтесь';
export const MSG_AUTH_TO_SIGN_IN = 'Уже есть аккаунт? Войдите';
export const MSG_AUTH_NICKNAME_HINT = 'От 3 до 16 символов: буквы, цифры, _ - . и пробелы.';
export const MSG_AUTH_PASSWORD_HINT = 'От 8 до 128 символов.';
/** The submit button while its request is in flight. */
export const MSG_AUTH_WAIT = 'Подождите…';

// ---------------------------------------------------------------- e-mail sign-in (where the server offers it)
/** The sign-in form's first field when the server offers e-mail sign-in: a nickname or a confirmed address. */
export const MSG_AUTH_NICKNAME_OR_EMAIL = 'Никнейм или e-mail';
export const MSG_AUTH_EMAIL = 'E-mail';
/** Under the sign-up form's e-mail field: the address is optional. */
export const MSG_AUTH_EMAIL_HINT = 'Необязательно: для входа по e-mail и восстановления пароля.';
export const MSG_AUTH_FORGOT = 'Забыли пароль?';
export const MSG_AUTH_FORGOT_TITLE = 'Восстановление пароля';
export const MSG_AUTH_FORGOT_TEXT = 'Введите подтверждённый e-mail аккаунта: мы пришлём ссылку, по которой можно задать новый пароль.';
export const MSG_AUTH_FORGOT_SEND = 'Прислать ссылку';
/** After the reset request: the same text whether or not the address has an account. */
export const MSG_AUTH_FORGOT_SENT = 'Если этот e-mail подтверждён в аккаунте, письмо со ссылкой уже в пути. Проверьте почту.';
export const MSG_AUTH_INVALID_EMAIL_CREDENTIALS = 'Неверный e-mail или пароль (или e-mail ещё не подтверждён).';
export const MSG_AUTH_EMAIL_LIMIT = 'Слишком много писем. Попробуйте позже.';
/** The message of a refused address (the same for the client-side check and the server's answer). */
export const EMAIL_PROBLEM_TEXT: Readonly<Record<EmailProblem, string>> = {
  email_required: 'Введите e-mail.',
  email_too_long: 'Слишком длинный e-mail.',
  email_invalid: 'Это не адрес e-mail.',
};

// ---------------------------------------------------------------- the e-mail of the account (settings: account dialog)
/** The account dialog's button (small) and the title of the e-mail dialog. */
export const MSG_EMAIL_BUTTON = 'E-mail';
export const MSG_EMAIL_TITLE = 'E-mail для входа';
/** The current sign-in address follows (data, never translated). */
export const MSG_EMAIL_CURRENT = 'Сейчас:';
export const MSG_EMAIL_NONE = 'E-mail не указан. С ним можно входить без никнейма и восстановить забытый пароль.';
/** The pending address follows. */
export const MSG_EMAIL_PENDING = 'Ждёт подтверждения:';
export const MSG_EMAIL_NEW = 'Новый e-mail';
export const MSG_EMAIL_PASSWORD = 'Пароль аккаунта';
export const MSG_EMAIL_SEND = 'Отправить ссылку';
export const MSG_EMAIL_RESEND = 'ещё раз';
export const MSG_EMAIL_SENT = 'Ссылка отправлена. Откройте письмо и подтвердите адрес.';
export const MSG_EMAIL_LOADING = 'Загрузка…';

// ---------------------------------------------------------------- account links the app was opened with (deep links)
export const MSG_LINK_VERIFIED_TITLE = 'E-mail подтверждён';
/** The confirmed address follows (partly hidden). */
export const MSG_LINK_VERIFIED = 'Теперь по этому адресу можно входить в игру:';
export const MSG_LINK_INVALID = 'Ссылка недействительна, уже использована или устарела. Попросите новую.';
export const MSG_LINK_TAKEN = 'Этот адрес тем временем подтвердил другой аккаунт.';
export const MSG_LINK_FAILED = 'Не удалось открыть ссылку. Попробуйте ещё раз.';
export const MSG_LINK_TITLE = 'Ссылка из письма';
export const MSG_LINK_OK = 'Хорошо';
export const MSG_LINK_RESET_TITLE = 'Новый пароль';
export const MSG_LINK_RESET_REPEAT = 'Повторите пароль';
export const MSG_LINK_RESET_MISMATCH = 'Пароли не совпадают.';
export const MSG_LINK_RESET_HINT = 'От 8 до 128 символов. Все устройства выйдут из аккаунта.';
export const MSG_LINK_RESET_SAVE = 'Сохранить';
export const MSG_LINK_RESET_DONE = 'Пароль изменён. Войдите с новым паролем.';

// ---------------------------------------------------------------- diagnostics and notifications (the app, account dialog)
export const MSG_DIAG_ON = 'Диагностика: вкл.';
export const MSG_DIAG_OFF = 'Диагностика: выкл.';
export const MSG_DIAG_NOTE_ON = 'Отчёты о сбоях и статистика без имени и e-mail включены. Спасибо!';
export const MSG_DIAG_NOTE_OFF = 'Диагностика выключена.';
export const MSG_PUSH_ON = 'Уведомления: вкл.';
export const MSG_PUSH_OFF = 'Уведомления: выкл.';
export const MSG_PUSH_NOTE_ON = 'Уведомления включены.';
export const MSG_PUSH_NOTE_OFF = 'Уведомления выключены.';
export const MSG_PUSH_DENIED = 'Уведомления запрещены в настройках телефона.';
export const MSG_PUSH_FAILED = 'Не удалось изменить уведомления. Попробуйте позже.';

// ---------------------------------------------------------------- the server setting (native app, dev server)
export const MSG_AUTH_SERVER = 'Сервер';
export const MSG_AUTH_SERVER_ADDRESS = 'Адрес сервера';
export const MSG_AUTH_SERVER_HINT = 'Например: 192.168.1.10:8787 (сервер в локальной сети), 10.0.2.2:8787 (компьютер для эмулятора Android) или https://fwp.example.org. Игра перезапустится.';
export const MSG_AUTH_SERVER_SAVE = 'Подключиться';
export const MSG_AUTH_SERVER_DEFAULT = 'по умолчанию';
/** The hint of a build that connects over TLS only (the Android release APK). */
export const MSG_AUTH_SERVER_HINT_SECURE = 'Например: https://fwp.example.org. Эта версия подключается только по https:// и wss://. Игра перезапустится.';
/** The loading screen of an app build without a default server, before the player named one. */
export const MSG_SERVER_NOT_SET = 'Сервер не указан. Введите адрес сервера.';
/** Why an address is refused (src/client/config.ts ServerAddressProblem). */
export const SERVER_PROBLEM_TEXT: Record<string, string> = {
  empty: 'Введите адрес сервера.',
  invalid: 'Это не адрес сервера.',
  protocol: 'Адрес должен начинаться с http://, https://, ws:// или wss://.',
  credentials: 'Адрес не может содержать имя пользователя или пароль.',
  insecure: 'Эта версия подключается только по https:// или wss://.',
};

// ---------------------------------------------------------------- the Android back button on the main map
export const MSG_EXIT_QUESTION = 'Выйти из игры?';
export const MSG_EXIT_YES = 'Выйти';
export const MSG_EXIT_NO = 'Остаться';

// ---------------------------------------------------------------- notices and the server's answers
export const MSG_AUTH_SESSION_EXPIRED = 'Сессия истекла. Войдите снова.';
/** The account signed in on another device (one active session per account): this device's session ended. */
export const MSG_AUTH_SESSION_REPLACED = 'Вы вошли в этот аккаунт на другом устройстве. Войдите снова, чтобы играть здесь.';
/** The sign-in button after that: signing in here ends the other device's session. */
export const MSG_AUTH_SIGN_IN_HERE = 'Войти здесь';
export const MSG_AUTH_SIGNED_OUT = 'Вы вышли из аккаунта.';
/** The sign-in screen after the player deleted the account (settings: delete account). */
export const MSG_AUTH_ACCOUNT_DELETED = 'Аккаунт удалён. Все его данные стёрты с сервера.';
export const MSG_AUTH_NICKNAME_TAKEN = 'Этот никнейм уже занят.';
export const MSG_AUTH_INVALID_CREDENTIALS = 'Неверный никнейм или пароль.';
export const MSG_AUTH_TOO_MANY_ATTEMPTS = 'Слишком много неудачных попыток входа. Попробуйте позже.';
export const MSG_AUTH_OFFLINE = 'Нет связи с сервером. Попробуйте ещё раз.';
export const MSG_AUTH_BUSY = 'Сервер перегружен. Попробуйте ещё раз.';
/** RATE_LIMITED too_many_accounts: the limit of new accounts per address (auth.signUp). */
export const MSG_AUTH_TOO_MANY_ACCOUNTS = 'С этого адреса создано слишком много аккаунтов. Попробуйте позже.';
export const MSG_AUTH_SERVER_ERROR = 'Ошибка сервера. Попробуйте ещё раз.';
export const MSG_AUTH_DISABLED = 'Вход по паролю на этом сервере отключён.';
export const MSG_AUTH_FAILED = 'Не удалось войти. Попробуйте ещё раз.';

/** The message of a refused nickname or password (the same for the client-side check and the server's answer). */
export const CREDENTIAL_PROBLEM_TEXT: Readonly<Record<CredentialProblemReason, string>> = {
  nickname_required: 'Введите никнейм.',
  nickname_too_short: 'Слишком короткий никнейм: нужно хотя бы 3 символа.',
  nickname_too_long: 'Слишком длинный никнейм: не больше 16 символов.',
  nickname_invalid: 'Только буквы одного алфавита, цифры, _ - . и одиночные пробелы; нужна хотя бы одна буква.',
  password_required: 'Введите пароль.',
  password_too_short: 'Слишком короткий пароль: нужно хотя бы 8 символов.',
  password_too_long: 'Слишком длинный пароль: не больше 128 символов.',
  password_invalid: 'Пароль не может состоять из одних пробелов или содержать служебные символы.',
};

// ---------------------------------------------------------------- the account in the game
/** Header of the hover popup of the profile plate (its text is the full nickname). */
export const MSG_NICKNAME_POPUP = 'Никнейм';
/** The sign-out button of the settings screen. */
export const MSG_SIGN_OUT = 'выйти';

// ---------------------------------------------------------------- deleting the account (settings screen)
/** The button under "sign out" on the settings screen. */
export const MSG_DELETE_ACCOUNT = 'удалить аккаунт';
export const MSG_DELETE_TITLE = 'Удаление аккаунта';
/** The account the dialog deletes: the nickname follows (data, never translated). */
export const MSG_DELETE_ACCOUNT_OF = 'Аккаунт:';
/** What goes (docs/backend.md §9.1) and that it cannot be undone. */
export const MSG_DELETE_WARNING =
  'Аккаунт и все его данные будут удалены с сервера навсегда: прогресс, золото и алмазы, оружие, реплеи, клан и места в рейтингах. Покупки не возвращаются. Отменить удаление нельзя.';
/** An account with a password confirms with it (account.delete). */
export const MSG_DELETE_PASSWORD = 'Введите пароль, чтобы подтвердить удаление';
/** A guest (an earlier version of the game) has no password: the session alone deletes it. */
export const MSG_DELETE_GUEST = 'У гостевого аккаунта нет пароля. После удаления вернуть его прогресс будет нельзя.';
export const MSG_DELETE_CONFIRM = 'Удалить навсегда';
export const MSG_DELETE_CANCEL = 'отмена';
export const MSG_DELETE_WRONG_PASSWORD = 'Неверный пароль.';
export const MSG_DELETE_TOO_MANY = 'Слишком много неверных паролей. Попробуйте позже.';
export const MSG_DELETE_FAILED = 'Не удалось удалить аккаунт. Попробуйте ещё раз.';
